import type { ProjectManifest } from "@pix/shared";

/**
 * manifest を読んで 論理 ID → URL に解決する。
 * Phaser の preload と組み合わせて使う。
 *
 * inMemoryAssets が指定されている場合、manifest のパスと一致するものは
 * dataURL を優先して返す(Studio からのライブ編集に対応)。
 */
export class AssetManager {
  private inMemoryAssets: Record<string, string> = {};

  constructor(
    private manifest: ProjectManifest,
    private baseUrl: string,
    inMemoryAssets?: Record<string, string>,
  ) {
    if (inMemoryAssets) this.inMemoryAssets = inMemoryAssets;
  }

  setInMemoryAssets(assets: Record<string, string>): void {
    this.inMemoryAssets = assets;
  }

  private resolve(path: string): string {
    if (this.inMemoryAssets[path]) return this.inMemoryAssets[path];
    // 組み込みアセット(builtin/)は engine 自身の origin に存在する。
    // baseUrl(プロジェクト配信元)とは独立に解決する。
    if (path.startsWith("builtin/")) {
      // 絶対パスを返すと、ブラウザは現在の document origin
      // (= play.pixadv.net もしくは localhost の engine dev) に対して解決する。
      return `/${path}`;
    }
    return `${this.baseUrl}/${path}`;
  }

  static async load(baseUrl: string): Promise<AssetManager> {
    const res = await fetch(`${baseUrl}/project.pix.json`);
    if (!res.ok) throw new Error(`manifest fetch failed: ${res.status}`);
    const manifest = (await res.json()) as ProjectManifest;
    return new AssetManager(manifest, baseUrl);
  }

  getManifest(): ProjectManifest {
    return this.manifest;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  // -------------------------- 背景

  getBgKey(name: string): string {
    return `bg:${name}`;
  }

  getBgUrl(name: string): string | null {
    const bg = this.manifest.backgrounds?.[name];
    if (!bg) return null;
    return this.resolve(bg.file);
  }

  listBgs(): Array<{ name: string; url: string }> {
    const out: Array<{ name: string; url: string }> = [];
    for (const name of Object.keys(this.manifest.backgrounds ?? {})) {
      const u = this.getBgUrl(name);
      if (u) out.push({ name, url: u });
    }
    return out;
  }

  // -------------------------- キャラ立ち絵

  getCharaSpriteKey(id: string, expression: string): string {
    return `chara:${id}:${expression}`;
  }

  getCharaSpriteUrl(id: string, expression: string): string | null {
    const c = this.manifest.characters?.[id];
    if (!c || !c.spritePattern) return null;
    const path = c.spritePattern.replaceAll("{expression}", expression).replaceAll("{id}", id);
    return this.resolve(path);
  }

  listCharaSprites(): Array<{ id: string; expression: string; url: string }> {
    const out: Array<{ id: string; expression: string; url: string }> = [];
    for (const [id, c] of Object.entries(this.manifest.characters ?? {})) {
      for (const expr of c.expressions ?? []) {
        const u = this.getCharaSpriteUrl(id, expr);
        if (u) out.push({ id, expression: expr, url: u });
      }
    }
    return out;
  }

  // -------------------------- フェイス

  getFaceKey(id: string, expression: string): string {
    return `face:${id}:${expression}`;
  }

  getFaceUrl(id: string, expression: string): string | null {
    const c = this.manifest.characters?.[id];
    if (!c || !c.facePattern) return null;
    const path = c.facePattern.replaceAll("{expression}", expression).replaceAll("{id}", id);
    return this.resolve(path);
  }

  listFaces(): Array<{ id: string; expression: string; url: string }> {
    const out: Array<{ id: string; expression: string; url: string }> = [];
    for (const [id, c] of Object.entries(this.manifest.characters ?? {})) {
      if (!c.facePattern) continue;
      for (const expr of c.expressions ?? []) {
        const u = this.getFaceUrl(id, expr);
        if (u) out.push({ id, expression: expr, url: u });
      }
    }
    return out;
  }

  // -------------------------- 音響

  getBgmUrl(name: string): string | null {
    const a = this.manifest.audio?.bgm?.[name];
    if (!a) return null;
    return this.resolve(a.file);
  }

  getSeUrl(name: string): string | null {
    const a = this.manifest.audio?.se?.[name];
    if (!a) return null;
    return this.resolve(a.file);
  }

  getVoiceUrl(name: string): string | null {
    const a = this.manifest.audio?.voice?.[name];
    if (!a) return null;
    return this.resolve(a.file);
  }

  // -------------------------- キャラ表示名

  getCharaName(id: string): string {
    return this.manifest.characters?.[id]?.name ?? id;
  }
}
